import React, { useState } from 'react';
import { X, Copy, CheckCircle, ExternalLink, Clock } from 'lucide-react';

interface DomainSetupLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  setupUrl: string;
  expiresAt: string;
  businessName: string;
}

export function DomainSetupLinkModal({
  isOpen,
  onClose,
  setupUrl,
  expiresAt,
  businessName,
}: DomainSetupLinkModalProps) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(setupUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleOpenInNewTab = () => {
    window.open(setupUrl, '_blank');
  };

  const formatExpiryDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">DNS Setup Link Gegenereerd</h2>
            <p className="text-sm text-gray-600 mt-1">
              Voor {businessName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Link verloopt op</p>
                <p className="text-sm text-blue-700 mt-1">
                  {formatExpiryDate(expiresAt)}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Setup Link
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={setupUrl}
                readOnly
                className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 font-mono"
                onClick={(e) => e.currentTarget.select()}
              />
              <button
                onClick={handleCopy}
                className="px-4 py-3 bg-[#FF6B35] text-white rounded-lg hover:bg-[#ff5722] transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Gekopieerd
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Kopieer
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Instructies</h3>
            <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
              <li>Deel deze link met de gebruiker via een veilig kanaal</li>
              <li>De gebruiker kan via deze link de DNS records bekijken en verifiëren</li>
              <li>Bij succesvolle verificatie wordt het support team automatisch geïnformeerd op support@dibbi.nl</li>
              <li>Verificatie is beperkt tot 1 keer per minuut om misbruik te voorkomen</li>
              <li>De link is 7 dagen geldig</li>
            </ol>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleOpenInNewTab}
              className="btn-primary flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Open in nieuw tabblad
            </button>
            <button
              onClick={onClose}
              className="btn-secondary"
            >
              Sluiten
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
